import { useCallback, useContext, useEffect, useState } from 'react';
import { Confirm, useAuthenticated, useNotify } from 'react-admin';
import { Loading, useDataProvider } from 'react-admin';
import { useLocation } from 'react-router-dom';

import { AppContext } from '@app/contexts';
import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { PAGE_TYPE, UNEDITABLE_SECTION_TYPES } from '@app/utils/railsUtils';
import update from 'immutability-helper';

import { HeaderAction, HeaderActionTypes } from '@app/components/ListHeaderActions';
import { TabbedGenericList } from '@app/components/common/GenericList';
import { ColumnTypes, DataSource, GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { useCallbackPrompt } from '../../hooks/useCallbackPrompt';

export const ListRails = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const [handleError] = useErrorHandling();
  const notify = useNotify();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type');

  console.log('Type=', type);

  useAuthenticated();
  const ability = useAbility();

  const dataProvider = useDataProvider();
  const [railsDataSource, setRailsDataSource] = useState<DataSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<string>((type?.toUpperCase() as string) || PAGE_TYPE.TODAY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(isDirty);
  const railsToDataSource = (rails: GenericObject[]) => {
    return [
      { source: 'TODAY', rows: rails.filter((r) => (r.page_type as string).toLowerCase() === 'today') },
      { source: 'DISCOVER', rows: rails.filter((r) => (r.page_type as string).toLowerCase() === 'discover') },
      { source: 'RETIRE', rows: rails.filter((r) => (r.page_type as string).toLowerCase() === 'retire') },
    ];
  };

  const getSelectedDataSource = (railsDataSource: DataSource[], selected: string) => {
    console.log('Getting selected data source');
    return railsDataSource.find((r) => r.source.toLowerCase() === selected.toLowerCase()) || ({} as DataSource);
  };

  const fetchRails = useCallback(() => {
    dataProvider
      .getList('rails', { pagination: { page: 0, perPage: 1000 }, sort: { field: '', order: '' }, filter: {} })
      .then(({ data }) => {
        setRailsDataSource(railsToDataSource(data));
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  }, [dataProvider]);

  useEffect(() => {
    fetchRails();
  }, [fetchRails]);

  const posStatusChange = () => {
    fetchRails();
  };

  const columns = [
    {
      label: 'NAME',
      key: 'title',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.title),
    },
    {
      label: 'RAIL TYPE',
      key: 'section_type',
      type: ColumnTypes.DEFAULT,
    },
    {
      label: 'PUBLISH DATE',
      key: 'publish_date',
      type: ColumnTypes.DATE,
    },
  ];

  if (ability.can(CASLAction.WRITE, 'rails')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'is_published',
      type: ColumnTypes.STATUS,
      // @ts-ignore this is definitely valid
      publishedKey: 'is_published',
      isPublished: (row: GenericObject) => !!row.is_published,
      getNewPublishedStatus: (row: GenericObject) => !row.is_published,
      posStatusChange,
    });
  }

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setIsDirty(true);
      setRailsDataSource((prevRailsDataSource) => {
        const selectedDataSource = getSelectedDataSource(prevRailsDataSource, selectedSource);
        selectedDataSource.rows = update(selectedDataSource.rows, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, selectedDataSource?.rows[dragIndex]],
          ],
        });
        return [...prevRailsDataSource];
      });
    },
    [selectedSource]
  );

  const additionalHeaderActions: HeaderAction[] | undefined = [];

  if (ability.can(CASLAction.REORDER, 'rails')) {
    additionalHeaderActions.push({
      id: 2,
      type: HeaderActionTypes.REORDER,
      disabled: !isDirty,
      handler: async () => {
        try {
          setLoading(true);
          const selectedDataSource = getSelectedDataSource(railsDataSource, selectedSource);
          const body = selectedDataSource.rows.map((rail) => rail.id);
          await dataProvider.reorder('rails', { body });
          notify('Rails have been reordered with success', { type: 'success' });
          setIsDirty(false);
          fetchRails();
        } catch (error: any) {
          handleError(error);
        } finally {
          setLoading(false);
        }
      },
    });
  }

  if (loading) return <Loading />;
  if (error) return <h1>{error}</h1>;
  if (!railsDataSource) return null;

  const deleteCallback = (id: number) => {
    setRailsDataSource((prevRailsDataSource) => {
      const selectedDataSource = getSelectedDataSource(prevRailsDataSource, selectedSource);
      selectedDataSource.rows = selectedDataSource.rows.filter((item) => item.id !== id);
      return [...prevRailsDataSource];
    });
  };

  const orderable = ability.can(CASLAction.REORDER, 'rails');

  // Avoided single-liner return to make it easier to read
  const isRowEditable = (row: GenericObject) => {
    // @ts-ignore rows of rails definitely have a section_type property
    if (UNEDITABLE_SECTION_TYPES.includes(row.section_type)) {
      return false;
    }
    return true;
  };

  return (
    <>
      <Confirm
        isOpen={showPrompt as boolean}
        title="Your changes haven't been saved"
        content="Are you sure you want to leave this page without saving?"
        onConfirm={confirmNavigation as () => void}
        cancel="Keep editing"
        confirm="Close without saving"
        onClose={cancelNavigation as () => void}
      />
      <TabbedGenericList
        isListLoading={loading}
        resourceSingular="rail"
        additionalHeaderActions={additionalHeaderActions}
        deleteCallback={deleteCallback}
        handleChangeLoading={handleChangeLoading}
        columns={columns}
        orderable={orderable}
        reorderRecord={moveCard}
        dataSources={railsDataSource}
        resource="rails"
        title="Rails"
        onSelectedSource={setSelectedSource}
        defaultSource={selectedSource}
        isRowEditable={isRowEditable}
      />
    </>
  );
};
